import { useSyncExternalStore } from 'react'

/* Connection state, read from the browser rather than inferred from failures.
 *
 * `navigator.onLine` is imperfect — it reports whether the machine has a
 * network interface, not whether our server is reachable — but it is the only
 * signal available BEFORE a request fails, and the bar needs to appear when the
 * connection drops rather than when the next save is attempted twenty minutes
 * later.
 *
 * useSyncExternalStore rather than useState plus useEffect: it reads the right
 * value on the FIRST paint, so a page loaded while already offline shows the
 * bar immediately instead of flashing "connected" for a frame.
 */

function subscribe(onChange: () => void) {
  window.addEventListener('online', onChange)
  window.addEventListener('offline', onChange)
  return () => {
    window.removeEventListener('online', onChange)
    window.removeEventListener('offline', onChange)
  }
}

function getSnapshot() {
  // Optional-chained because jsdom and older environments may not define it,
  // and "assume online" is the safer default: a bar that appears wrongly is
  // more damaging to trust than one that appears late.
  return navigator?.onLine ?? true
}

/** True when the browser believes it has a network connection. */
export function useIsOnline(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, () => true)
}
